import { css, styled } from '@mui/material';

import Skeleton from '../Skeleton/Skeleton';
import { skeletonClasses } from '../Skeleton/theme';
import { tableSkeletonClasses } from './theme';

import type { TableSkeletonOwnerState, TableSkeletonProps } from './types';

export default function TableSkeleton(props: TableSkeletonProps) {
  const {
    pageSize = 20,
    showCheckboxColumn = true,
    showSideMargin = false,
    size = 'medium',
    ...others
  } = props;

  const ownerState: TableSkeletonOwnerState = {
    ...props,
    pageSize,
    showCheckboxColumn,
    showSideMargin,
    size,
  };

  const rows = Array(pageSize).fill(1);
  const cells = Array(15).fill(1);

  return (
    <TableSkeletonRoot {...others} ownerState={ownerState}>
      <TableSkeletonHead ownerState={ownerState}>
        <TableSkeletonRow ownerState={ownerState}>
          {cells.map((_, i) => (
            <TableSkeletonCell key={i} ownerState={ownerState}>
              <Skeleton shape="text" />
            </TableSkeletonCell>
          ))}
        </TableSkeletonRow>
      </TableSkeletonHead>
      <TableSkeletonBody ownerState={ownerState}>
        {rows.map((_, i) => (
          <TableSkeletonRow key={i} ownerState={ownerState}>
            {cells.map((_, j) => (
              <TableSkeletonCell key={j} ownerState={ownerState}>
                <Skeleton shape="text" />
              </TableSkeletonCell>
            ))}
          </TableSkeletonRow>
        ))}
      </TableSkeletonBody>
    </TableSkeletonRoot>
  );
}

const TableSkeletonRoot = styled('div', {
  target: tableSkeletonClasses.root,
})<{ ownerState: TableSkeletonOwnerState }>(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    border-top: ${theme.mixins.border('border/weaker/default')};
    border-bottom: ${theme.mixins.border('border/weaker/default')};
  `,
);

const TableSkeletonHead = styled('div', {
  target: tableSkeletonClasses.head,
})<{ ownerState: TableSkeletonOwnerState }>(() => css``);

const TableSkeletonBody = styled('div', {
  target: tableSkeletonClasses.body,
})<{ ownerState: TableSkeletonOwnerState }>(() => css``);

const TableSkeletonRow = styled('div', {
  target: tableSkeletonClasses.row,
})<{ ownerState: TableSkeletonOwnerState }>(
  ({ theme, ownerState }) => css`
    display: flex;
    align-items: center;
    .${tableSkeletonClasses.head} & {
      height: 40px;
    }
    .${tableSkeletonClasses.body} & {
      ${ownerState.size === 'small' &&
      css`
        height: 40px;
      `}
      ${ownerState.size === 'medium' &&
      css`
        height: 48px;
      `}
    }

    &:not(.${tableSkeletonClasses.body} &:last-of-type) {
      border-bottom: ${theme.mixins.border('border/weaker/default')};
    }
  `,
);

const TableSkeletonCell = styled('div', {
  target: tableSkeletonClasses.cell,
})<{ ownerState: TableSkeletonOwnerState }>(
  ({ ownerState }) => css`
    padding: 0px 16px;

    & .${skeletonClasses.root} {
      width: 300px;
      height: 12px;
    }

    ${ownerState.showCheckboxColumn &&
    css`
      &:first-of-type .${skeletonClasses.root} {
        width: 16px;
        height: 16px;
      }
    `}

    ${ownerState.showSideMargin &&
    css`
      &:first-of-type {
        padding-left: calc(20px + 16px);
      }
      &:last-of-type {
        padding-right: calc(20px + 16px);
      }
    `}
  `,
);
